import styled from "@emotion/styled";
import { useState, useEffect } from "react";
import { Form, Formik } from "formik";
import DefaultField from "./common/DefaultField";
import {
  existingSchema,
  notExistingSchema,
  repoNonAdenSchema,
} from "../utils/schemas";
import { Icon } from "@iconify/react";
import {
  getCountries,
  postAttended,
  postAttendedRegister,
} from "../api/events";
import DefaultComplete from "./common/DefaultComplete";
import InputPhone from "./common/InputPhone";
import { useMatch } from "react-router-dom";
import {
  getRepoBadge,
  getRepoSurveyPublic,
  patchRepoAttended,
  postRepoAttendedRegister,
  postRepoCreateBadge,
} from "../api/repo-events";
import HighFive from "../assets/high-five.png";
import CycleBadge from "../assets/insignia.png";
import { Download } from "@mui/icons-material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const FormComponent = (props) => {
  const { event } = props;
  const isRepoEvent = !!useMatch("/evento/repo/:id");
  const hasSurvey = !!event?.survey_url;
  const hasCycleId = !!event?.cycle_id;
  const isLastClass = event?.is_last_class;
  const [isRegistered, setIsRegistered] = useState(true);
  const [isAttendanceMarked, setIsAttendanceMarked] = useState(false);
  const [surveyOpened, setSurveyOpened] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState(null);
  const [missingEvents, setMissingEvents] = useState([]);
  const [cycleBadge, setCycleBadge] = useState(null);
  const [requiresFullRegistration, setRequiresFullRegistration] =
    useState(false);
  const [surveyAvailable, setSurveyAvailable] = useState(null);

  // EFFECTS
  useEffect(() => {
    countries === null && handleCountries();
  }, [countries]);

  useEffect(() => {
    if (
      !isRepoEvent ||
      !isAttendanceMarked ||
      surveyAvailable !== true ||
      !userEmail ||
      cycleBadge
    ) {
      return;
    }

    const markSurveyOpened = async () => {
      try {
        await patchRepoAttended(event.id, {
          has_answered_survey: true,
          email: userEmail,
        });
      } catch (error) {}
    };

    markSurveyOpened();
  }, [
    isRepoEvent,
    isAttendanceMarked,
    surveyAvailable,
    userEmail,
    cycleBadge,
    event,
  ]);

  // FUNCTIONS
  const handleCountries = async () => {
    try {
      const country = await getCountries();

      setCountries(country);
    } catch (error) {
      setCountries(false);
    }
  };

  const initialValues =
    isRepoEvent && requiresFullRegistration
      ? {
          email: "",
          name: "",
          phone: "",
        }
      : isRegistered || isRepoEvent
        ? {
            email: "",
          }
        : {
            email: "",
            name: "",
            lastName: "",
            country: "",
            phone: "",
          };

  const resolveRepoSurvey = async (email) => {
    try {
      await getRepoSurveyPublic(event.id, email);
      setSurveyAvailable(true);
      window.dispatchEvent(new CustomEvent("confirmEvent"));
    } catch (error) {
      setSurveyAvailable(false);
    }
  };

  const handleSubmit = async (e) => {
    setLoading(true);
    const confirmEvent = new CustomEvent("confirmEvent");
    try {
      if (isRegistered) {
        const response = isRepoEvent
          ? await patchRepoAttended(event.id, { state: "done", email: e.email })
          : await postAttended(event.crm_id, e.email);

        if (!!response?.error) {
          setUserEmail("");
          setIsRegistered(false);
          setIsAttendanceMarked(false);
          setSurveyAvailable(null);
          setLoading(false);
        } else {
          setUserEmail(e.email);
          setIsRegistered(true);
          setIsAttendanceMarked(true);
          if (isRepoEvent) {
            await resolveRepoSurvey(e.email);
          } else {
            window.dispatchEvent(confirmEvent);
          }
          setLoading(false);
        }
      } else {
        if (isRepoEvent) {
          const body = requiresFullRegistration
            ? { email: e.email, name: e.name, phone: e.phone }
            : { email: e.email };
          await postRepoAttendedRegister(event.id, body);
          await patchRepoAttended(event.id, { state: "done", email: e.email });
        } else {
          await postAttendedRegister({
            event_id: event?.crm_id,
            registration_data: {
              name: e.name,
              lastname: e.lastName,
              email: e.email,
              phone: e.phone,
              country: e?.country?.code,
            },
          });
          await sendContact(e?.email);
        }

        setUserEmail(e.email);
        setIsAttendanceMarked(true);
        setIsRegistered(true);
        if (isRepoEvent) {
          await resolveRepoSurvey(e.email);
        } else {
          window.dispatchEvent(confirmEvent);
        }
        setLoading(false);
      }
    } catch (error) {
      const d = error?.response?.data?.detail;
      const apiMsg =
        typeof d === "string"
          ? d
          : d && typeof d.message === "string"
            ? d.message
            : typeof error?.message === "string" &&
                error.message !== "[object Object]"
              ? error.message
              : "";
      if (isRepoEvent && apiMsg && /no pertenece a.*ADEN/i.test(apiMsg)) {
        setRequiresFullRegistration(true);
      } else {
        setRequiresFullRegistration(false);
      }
      setIsRegistered(false);
      setIsAttendanceMarked(false);
      setSurveyAvailable(null);
      setLoading(false);
    }
  };

  const sendContact = async (email) => {
    try {
      await postAttended(event?.crm_id, email);
    } catch (error) {
      setLoading(false);
      setIsAttendanceMarked(false);
    }
  };

  const handleOpenSurvey = async () => {
    try {
      const response = await postAttended(event.crm_id, userEmail);

      if (!!response?.error) {
        setSurveyOpened(false);
      } else {
        setSurveyOpened(true);
      }
    } catch (error) {
      setSurveyOpened(false);
    }
  };

  const handleCycle = async () => {
    setLoading(true);
    try {
      await postRepoCreateBadge(event.cycle_id, { email: userEmail });
      const response = await getRepoBadge(event.cycle_id, userEmail);
      setCycleBadge(response);
    } catch (error) {
      setMissingEvents(JSON.parse(error.message).missing_events);
    } finally {
      setLoading(false);
    }
  };

  const handleViewBadge = () => {
    if (cycleBadge?.html) {
      const badgeWindow = window.open("", "_blank");
      const centeredHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>Insignia de Ciclo - ADEN</title>
            <style>
              body {
                margin: 0;
                padding: 20px;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
                background-color: #f5f5f5;
              }
              .badge-container {
                max-width: 100%;
                box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                background: white;
              }
            </style>
          </head>
          <body>
            <div class="badge-container">
              ${cycleBadge.html}
            </div>
          </body>
        </html>
      `;

      badgeWindow.document.write(centeredHTML);
      badgeWindow.document.close();
    }
  };

  const handleDownloadBadge = async () => {
    const element = document.createElement("div");
    element.innerHTML = cycleBadge.html;
    element.style.padding = "20px";
    element.style.position = "absolute";
    element.style.left = "-9999px";
    element.id = "toPdf";
    document.body.appendChild(element);

    window.html2canvas = html2canvas;

    const options = {
      background: "white",
      scrollY: -window.scrollY,
      scrollX: -window.scrollX,
      scale: 2,
    };

    try {
      const canvas = await html2canvas(element, options);
      document.body.removeChild(element);

      const img = canvas.toDataURL("image/PNG");
      let doc = new jsPDF("l", "cm", "a4");

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const widthRatio = pageWidth / canvas.width;
      const heightRatio = pageHeight / canvas.height;
      const ratio = widthRatio > heightRatio ? heightRatio : widthRatio;

      const canvasWidth = canvas.width * ratio;
      const canvasHeight = canvas.height * ratio;

      const marginX = (pageWidth - canvasWidth) / 2;
      const marginY = (pageHeight - canvasHeight) / 2;

      doc.addImage(
        img,
        "PNG",
        marginX,
        marginY,
        canvasWidth,
        canvasHeight,
        undefined,
        "FAST",
      );

      doc.save("insignia-ciclo-aden.pdf");
    } catch (error) {
      console.error("Error generando PDF:", error);
      document.body.removeChild(element);
    }
  };

  const errorMessage =
    "¡Upss! No estás inscripto en el evento, completa los campos a continuación para registrarte y confirmar asistencia.";

  return (
    <Wrapper>
      {!isAttendanceMarked && (
        <h4>Ingresa el mail con el que te inscribiste al evento:</h4>
      )}
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={
          isRepoEvent && requiresFullRegistration
            ? repoNonAdenSchema
            : isRegistered || isRepoEvent
              ? existingSchema
              : notExistingSchema
        }
      >
        {({ values, setFieldValue }) => (
          <Container>
            {!isAttendanceMarked && (
              <DefaultField name="email" type="text" label="E-mail" />
            )}
            {isAttendanceMarked &&
              !cycleBadge &&
              (!isRepoEvent || surveyAvailable === false) && (
              <>
                <img src={HighFive} alt="High Five" />
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "#b31d15",
                  }}
                >
                  ¡Tu asistencia ha sido registrada correctamente!
                </p>
                <p style={{ textAlign: "center", fontSize: "18px" }}>
                  ¡Gracias por elegirnos!
                </p>
              </>
            )}
            {isAttendanceMarked &&
              !isRepoEvent &&
              hasSurvey &&
              !surveyOpened &&
              !cycleBadge && (
                <CustomButton
                  type="button"
                  onClick={handleOpenSurvey}
                  fullWidth
                  isFilled={true}
                >
                  Responder encuesta
                </CustomButton>
              )}
            {isAttendanceMarked &&
              !isRepoEvent &&
              surveyOpened &&
              !cycleBadge &&
              userEmail && (
                <SurveyIframe
                  src={`https://app.aden.org/survey/event/${event?.id}?email=${encodeURIComponent(userEmail)}`}
                  title="Encuesta de satisfacción del evento"
                  allow="clipboard-write"
                />
              )}
            {isAttendanceMarked &&
              isRepoEvent &&
              surveyAvailable === true &&
              !cycleBadge &&
              userEmail && (
                <SurveyIframe
                  src={`https://app.aden.org/survey/event/${event?.id}?email=${encodeURIComponent(userEmail)}`}
                  title="Encuesta de satisfacción del evento"
                  allow="clipboard-write"
                />
              )}
            {hasCycleId && isLastClass && isAttendanceMarked && !cycleBadge && (
              <>
                <CustomButton
                  type="button"
                  onClick={handleCycle}
                  disabled={loading}
                  isFilled={
                    isRepoEvent
                      ? true
                      : hasSurvey && !surveyOpened
                        ? false
                        : true
                  }
                >
                  {loading ? "Solicitando..." : "Solicitar insignia de ciclo"}
                </CustomButton>
                {missingEvents.length === 0 && (
                  <Warning>
                    <IconWarning icon="material-symbols-light:info-outline-rounded" />
                    <p>
                      Para obtener la insignia del ciclo, debes haber asistido a
                      todas las clases que conforman el ciclo.
                    </p>
                  </Warning>
                )}
                {missingEvents.length > 0 && (
                  <>
                    <Warning>
                      <IconWarning icon="material-symbols-light:info-outline-rounded" />
                      <p>
                        Para obtener la insignia te falta asistir a las
                        siguientes clases:
                      </p>
                    </Warning>
                    <ul>
                      {missingEvents.map((event, index) => (
                        <li key={index}>{event}</li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
            {isRegistered && (
              <>
                <ButtonsContainer>
                  {!isAttendanceMarked && (
                    <CustomButton
                      disabled={loading}
                      type="submit"
                      fullWidth
                      isFilled={true}
                    >
                      {loading
                        ? "Verificando usuario..."
                        : isAttendanceMarked
                          ? "Asistencia confirmada"
                          : "Marcar asistencia"}
                    </CustomButton>
                  )}
                </ButtonsContainer>
                {isAttendanceMarked && !isRepoEvent && (
                  <>
                    <CustomButton
                      onClick={() => {
                        window.open(
                          `https://live.aden.org/certificado/${event?.crm_id}`,
                        );
                      }}
                      style={{ backgroundColor: "#25c159" }}
                    >
                      Constancia de participación
                    </CustomButton>
                  </>
                )}
              </>
            )}
            {!isRegistered && (
              <FormContainer>
                <Warning>
                  <IconWarning icon="material-symbols-light:info-outline-rounded" />
                  <p>{errorMessage}</p>
                </Warning>

                {!isRepoEvent && (
                  <>
                    <Row>
                      <DefaultField name="name" type="text" label="NOMBRE" />
                      <DefaultField
                        name="lastName"
                        type="text"
                        label="APELLIDO"
                      />
                    </Row>
                    <DefaultComplete
                      name="country"
                      requiredType={true}
                      label="PAÍS"
                      value={values.country}
                      setFieldValue={setFieldValue}
                      options={countries}
                    />
                    <InputPhone
                      name="phone"
                      label="NÚMERO DE TELÉFONO"
                      value={values.phone}
                      setFieldValue={setFieldValue}
                      variant="outlined"
                      required={true}
                    />
                  </>
                )}
                {isRepoEvent && requiresFullRegistration && (
                  <>
                    <DefaultField
                      name="name"
                      type="text"
                      label="NOMBRE Y APELLIDO"
                    />
                    <InputPhone
                      name="phone"
                      label="NÚMERO DE TELÉFONO"
                      value={values.phone}
                      setFieldValue={setFieldValue}
                      variant="outlined"
                      required={true}
                    />
                  </>
                )}
                <CustomButton
                  disabled={loading}
                  fullWidth
                  attendanceMarked={isAttendanceMarked}
                  type="submit"
                >
                  {loading
                    ? "Registrando usuario..."
                    : isAttendanceMarked
                      ? "Asistencia confirmada"
                      : "Registrarme y marcar asistencia"}
                </CustomButton>
                {isAttendanceMarked && !isRepoEvent && (
                  <CustomButton
                    fullWidth
                    onClick={() => {
                      window.open(
                        `https://live.aden.org/certificado/${event?.crm_id}`,
                      );
                    }}
                  >
                    Constancia de participación
                  </CustomButton>
                )}
              </FormContainer>
            )}
          </Container>
        )}
      </Formik>
      {cycleBadge && (
        <BadgeContainer>
          <img
            src={CycleBadge}
            alt="Insignia de ciclo"
            style={{ width: "200px", height: "200px" }}
          />
          <p
            style={{
              textAlign: "center",
              fontSize: "18px",
              fontWeight: "bold",
            }}
          >
            INSIGNIA DE CICLO
          </p>
          <BadgeButtonsContainer>
            <CustomButton
              onClick={handleViewBadge}
              style={{ width: "250px" }}
              isFilled={true}
            >
              Ver insignia
            </CustomButton>
            <CustomButton
              onClick={handleDownloadBadge}
              style={{ width: "250px" }}
              isFilled={false}
            >
              <Download />
              Descargar Insignia
            </CustomButton>
          </BadgeButtonsContainer>
        </BadgeContainer>
      )}
    </Wrapper>
  );
};

export default FormComponent;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  margin-bottom: 5rem;

  h4 {
    color: #363635;
    font-size: 1rem;
    font-weight: 400;
    text-align: center;
  }

  h5 {
    color: #b31d15;
    font-size: 0.8rem;
    text-align: center;
  }
`;

const Container = styled(Form)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-direction: column-reverse;
  align-items: center;
`;

const CustomButton = styled.button`
  width: ${(p) => (p.fullWidth ? "100%" : "fit-content")};
  background-color: ${(p) => (p.isFilled ? "#b31d15" : "#fff")};
  color: ${(p) => (p.isFilled ? "#fff" : "#b31d15")};
  border: 1px solid #b31d15;
  font-size: 0.95rem;
  font-weight: bold;
  border-radius: 30px;
  cursor: pointer;
  padding: 1rem 1.5rem;
  opacity: ${(p) => (p.disabled ? 0.6 : 1)};
  ${(p) => p.disabled && "pointer-events: none"};
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;
const Warning = styled.div`
  display: flex;
  gap: 0.3rem;
  align-items: center;
  max-width: 400px;

  p {
    color: #1d1d1b;
    font-size: 1rem;
  }
`;

const IconWarning = styled(Icon)`
  font-size: 3rem;
  color: #b31d15;
`;

const Row = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const BadgeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
`;

const BadgeButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-direction: column;
  align-items: center;

  button:last-child {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }
`;

const SurveyIframe = styled.iframe`
  width: 95vw;
  height: 90vh;
  border: none;
  border-radius: 20px;
  background-color: #fff;
  position: relative;
  left: 50%;
  transform: translateX(-50%);
`;
